import React from "react";
import { Box, Container, Typography, Button } from "@mui/material";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import HeroBackground from "./HeroBackground";

const INK = "#0f1121";
const INK_SOFT = "rgba(15, 17, 33, 0.62)";
const INK_FAINT = "rgba(15, 17, 33, 0.42)";
const INK_HAIRLINE = "rgba(15, 17, 33, 0.08)";
const ACCENT = "#3854e8";
const SURFACE = "#ffffff";

const SANS = "'sohne-var', 'Helvetica Neue', 'Arial', sans-serif";
const MONO =
  "'JetBrains Mono', 'Söhne Mono', ui-monospace, SFMono-Regular, Menlo, monospace";

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.09, delayChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 18 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 110, damping: 22 }
  }
};

const primaryButtonSx = {
  fontSize: "0.98rem",
  fontWeight: 600,
  fontFamily: SANS,
  textTransform: "none",
  letterSpacing: "0.2px",
  px: 3.5,
  py: 1.4,
  borderRadius: "12px",
  color: "#fafaf7",
  backgroundColor: INK,
  boxShadow:
    "inset 0 1px 0 rgba(255, 255, 255, 0.08), 0 8px 18px -10px rgba(15, 17, 33, 0.45)",
  transition:
    "transform 0.15s ease, box-shadow 0.2s ease, background-color 0.2s ease",
  "&:hover": {
    backgroundColor: "#1a1d36",
    transform: "translateY(-1px)"
  },
  "&:active": { transform: "translateY(0) scale(0.985)" }
};

const ghostButtonSx = {
  fontSize: "0.95rem",
  fontWeight: 500,
  fontFamily: SANS,
  textTransform: "none",
  letterSpacing: "0.2px",
  px: 2.5,
  py: 1.35,
  borderRadius: "12px",
  color: INK_SOFT,
  border: `1px solid ${INK_HAIRLINE}`,
  backgroundColor: "rgba(255, 255, 255, 0.6)",
  backdropFilter: "blur(6px)",
  transition: "all 0.15s ease",
  "&:hover": {
    color: INK,
    borderColor: "rgba(15, 17, 33, 0.32)",
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    transform: "translateY(-1px)"
  },
  "&:active": { transform: "translateY(0) scale(0.985)" }
};

const HeroSection = () => {
  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        textAlign: "left",
        minHeight: { xs: 560, md: "min(720px, 92vh)" }
      }}
    >
      <HeroBackground height="100%" />

      <Container
        maxWidth="xl"
        sx={{
          position: "relative",
          zIndex: 1,
          px: { xs: 3, sm: 4, md: 6 },
          pt: { xs: 8, md: 14 },
          pb: { xs: 10, md: 16 }
        }}
      >
        <Box
          component={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate="show"
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 7fr) 5fr" },
            gap: { xs: 3, md: 6 },
            alignItems: "center"
          }}
        >
          <Box>
            <Box
              component={motion.div}
              variants={itemVariants}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1.25,
                mb: 3,
                px: 1.5,
                py: 0.6,
                borderRadius: "999px",
                border: `1px solid ${INK_HAIRLINE}`,
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                backdropFilter: "blur(8px)"
              }}
            >
              <Box
                component={motion.span}
                animate={{ opacity: [0.55, 1, 0.55] }}
                transition={{
                  duration: 2.4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  backgroundColor: ACCENT,
                  display: "inline-block"
                }}
              />
              <Typography
                sx={{
                  fontFamily: MONO,
                  fontSize: "0.7rem",
                  fontWeight: 500,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: INK_SOFT
                }}
              >
                Interview practice studio
              </Typography>
            </Box>

            <Typography
              component={motion.h1}
              variants={itemVariants}
              sx={{
                fontFamily: SANS,
                fontWeight: 600,
                fontSize: { xs: "2.6rem", sm: "3.2rem", md: "3.8rem", lg: "4.2rem" },
                lineHeight: 1.02,
                letterSpacing: "-0.03em",
                color: INK,
                m: 0,
                mb: 3,
                maxWidth: "18ch"
              }}
            >
              Practice the interview you're about to walk into.
            </Typography>

            <Typography
              component={motion.p}
              variants={itemVariants}
              sx={{
                fontFamily: SANS,
                fontSize: { xs: "1.05rem", md: "1.18rem" },
                lineHeight: 1.55,
                color: INK_SOFT,
                m: 0,
                mb: 4,
                maxWidth: "52ch"
              }}
            >
              Record real answers in your own voice. Get sharp written feedback in
              seconds — what to keep, what to rework, what to say next time.
            </Typography>

            <Box
              component={motion.div}
              variants={itemVariants}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap"
              }}
            >
              <Button
                component={Link}
                to="/job"
                variant="contained"
                disableRipple
                endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 18 }} />}
                sx={primaryButtonSx}
              >
                Start a session
              </Button>
              <Button
                component={Link}
                to="/login"
                disableRipple
                sx={ghostButtonSx}
              >
                Sign in
              </Button>
            </Box>

          </Box>

          <Box
            component={motion.div}
            variants={itemVariants}
            sx={{
              display: { xs: "none", md: "flex" },
              flexDirection: "column",
              gap: 1.5,
              ml: { md: 2 }
            }}
          >
            <Box
              sx={{
                position: "relative",
                backgroundColor: SURFACE,
                borderRadius: "20px",
                border: `1px solid ${INK_HAIRLINE}`,
                boxShadow:
                  "inset 0 1px 0 rgba(255, 255, 255, 0.6), 0 24px 60px -28px rgba(15, 17, 33, 0.22), 0 6px 14px -6px rgba(15, 17, 33, 0.08)",
                p: 2.5
              }}
            >
              <Typography
                sx={{
                  fontFamily: MONO,
                  fontSize: "0.66rem",
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: INK_FAINT,
                  mb: 1.25
                }}
              >
                Question 03 · 10
              </Typography>
              <Typography
                sx={{
                  fontFamily: SANS,
                  fontSize: "1.02rem",
                  fontWeight: 500,
                  lineHeight: 1.35,
                  color: INK,
                  mb: 2
                }}
              >
                Walk me through a project you led end to end.
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  py: 1.25,
                  px: 1.5,
                  borderRadius: "12px",
                  border: `1px solid ${INK_HAIRLINE}`,
                  backgroundColor: "rgba(15, 17, 33, 0.02)"
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    backgroundColor: INK,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fafaf7"
                  }}
                >
                  <PlayArrowRoundedIcon sx={{ fontSize: 16 }} />
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    height: 3,
                    borderRadius: "999px",
                    backgroundColor: INK_HAIRLINE,
                    position: "relative",
                    overflow: "hidden"
                  }}
                >
                  <Box
                    component={motion.div}
                    animate={{ width: ["0%", "78%", "0%"] }}
                    transition={{
                      duration: 6,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      bottom: 0,
                      backgroundColor: INK,
                      borderRadius: "999px"
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontFamily: MONO,
                    fontSize: "0.74rem",
                    color: INK_SOFT,
                    fontVariantNumeric: "tabular-nums"
                  }}
                >
                  01:24
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                position: "relative",
                backgroundColor: SURFACE,
                borderRadius: "20px",
                border: `1px solid ${INK_HAIRLINE}`,
                boxShadow:
                  "inset 0 1px 0 rgba(255, 255, 255, 0.6), 0 24px 60px -28px rgba(15, 17, 33, 0.22), 0 6px 14px -6px rgba(15, 17, 33, 0.08)",
                overflow: "hidden"
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  py: 1.4,
                  px: 2.25,
                  borderBottom: `1px solid ${INK_HAIRLINE}`,
                  backgroundColor: "rgba(15, 17, 33, 0.02)"
                }}
              >
                <Typography
                  sx={{
                    fontFamily: SANS,
                    fontSize: "0.86rem",
                    fontWeight: 600,
                    color: INK
                  }}
                >
                  Written feedback
                </Typography>
                <Typography
                  sx={{
                    fontFamily: MONO,
                    fontSize: "0.66rem",
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    color: INK_FAINT
                  }}
                >
                  AI · 2 sec ago
                </Typography>
              </Box>
              <Box sx={{ px: 2.25, py: 0.5 }}>
                {[
                  {
                    label: "Strengths",
                    count: 2,
                    accent: "#1f8a5a",
                    accentSoft: "rgba(31, 138, 90, 0.12)",
                    items: [
                      "Clear narrative arc — problem, constraint, result.",
                      "Specific metric anchored the impact (cut p95 by 38%)."
                    ]
                  },
                  {
                    label: "Watch-outs",
                    count: 1,
                    accent: "#c25a1f",
                    accentSoft: "rgba(194, 90, 31, 0.12)",
                    items: [
                      "You glossed over the team — interviewers will ask who did what."
                    ]
                  },
                  {
                    label: "Next time",
                    count: 2,
                    accent: ACCENT,
                    accentSoft: "rgba(56, 84, 232, 0.12)",
                    items: [
                      "Name the trade-off you rejected before landing on the chosen design.",
                      "Close with what you'd do differently with another quarter."
                    ]
                  }
                ].map((s, i) => (
                  <Box
                    key={s.label}
                    sx={{
                      py: 1.5,
                      pl: 1.75,
                      borderLeft: `2px solid ${s.accent}`
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: MONO,
                          fontSize: "0.66rem",
                          fontWeight: 500,
                          letterSpacing: "0.14em",
                          color: INK_FAINT,
                          fontVariantNumeric: "tabular-nums"
                        }}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: SANS,
                          fontSize: "0.74rem",
                          fontWeight: 600,
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          color: INK
                        }}
                      >
                        {s.label}
                      </Typography>
                      <Box
                        sx={{
                          px: 0.85,
                          py: 0.15,
                          borderRadius: "999px",
                          backgroundColor: s.accentSoft,
                          color: s.accent,
                          fontFamily: MONO,
                          fontSize: "0.62rem",
                          fontWeight: 500,
                          letterSpacing: "0.06em",
                          fontVariantNumeric: "tabular-nums"
                        }}
                      >
                        {s.count}
                      </Box>
                    </Box>
                    <Box
                      component="ul"
                      sx={{
                        m: 0,
                        pl: 2,
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.6
                      }}
                    >
                      {s.items.map((item, j) => (
                        <Box
                          component="li"
                          key={j}
                          sx={{
                            fontFamily: SANS,
                            fontSize: "0.86rem",
                            lineHeight: 1.45,
                            color: INK_SOFT,
                            "&::marker": { color: s.accent }
                          }}
                        >
                          {item}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default HeroSection;
